/**
 * Shared HTML sanitization helpers for CMS-authored content.
 *
 * All raw-HTML render sites in the dashboard MUST route through one of these
 * functions before passing a string to React's HTML injection prop, or before
 * assigning to innerHTML for measurement / truncation.
 *
 * Allow-list approach (block-list is not used). Anything not on the allow-list
 * is stripped at render time, so existing payloads stored in the database
 * become inert at the boundary without a data migration.
 */

import DOMPurify from 'isomorphic-dompurify'
import { isDocsLinkHref, isHttpsUrl } from '@school-journey/shared-types'

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'img',
  'blockquote',
  'code',
  'pre',
  'span',
  'div',
  'hr',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th'
]

const ALLOWED_ATTR = ['href', 'src', 'alt', 'title', 'class', 'target', 'rel']

const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i

const FORBID_TAGS = ['script', 'iframe', 'object', 'embed', 'style', 'link', 'meta', 'base', 'form', 'input', 'button']

/**
 * Sanitize CMS-authored rich-text HTML for safe render.
 *
 * Strips: script/iframe/object/embed/style/link/meta/base/form/input/button,
 * all on* event handlers, and any URL whose protocol is not
 * http/https/mailto/tel.
 */
export function sanitizeContentHtml(input: string | null | undefined): string {
  if (!input) return ''
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    FORBID_TAGS,
    FORBID_ATTR: [],
    KEEP_CONTENT: true,
    USE_PROFILES: { html: true }
  })
}

// ============================================
// Blog profile — baked BlockNote post HTML
// ============================================

/**
 * Tags BlockNote's `blocksToFullHTML` exporter can emit. Deliberately a SEPARATE
 * list from `ALLOWED_TAGS` above: `figure` / `figcaption` are the aligned-image
 * block's wrapper and are not part of the generic CMS profile.
 *
 * Mirrors `apps/backend/src/services/blog/render/sanitize-rendered-html.ts` —
 * the publish-time sanitizer. If the two disagree, published HTML renders
 * differently from the admin's own draft preview, because the draft preview is
 * baked in the browser and never passes through the backend one.
 *
 * NO TABLE TAGS, deliberately. They sat here on loan between U3 and U7, while
 * the docs surface rendered through this profile; `DOCS_ALLOWED_TAGS` below owns
 * them now. `BLOG_BLOCK_TYPES` has no table block and the backend sanitizer
 * names none, so a table reaching this profile is markup no blog post can
 * produce — pinned in `__tests__/html.test.ts`.
 */
const BLOG_ALLOWED_TAGS = [
  'p',
  'br',
  'hr',
  'div',
  'span',
  'a',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'del',
  'mark',
  'sub',
  'sup',
  'code',
  'pre',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'img',
  'figure',
  'figcaption'
]

/**
 * `class` carries BlockNote's `bn-*` styling hooks; `data-*` (allowed by
 * DOMPurify's `ALLOW_DATA_ATTR`, on by default) carries `data-content-type`,
 * which every `.blog-post-body` rule in `globals.css` keys off. Stripping either
 * renders the post unstyled.
 *
 * `style` is NOT here, matching the backend: the blog template owns presentation.
 */
const BLOG_ALLOWED_ATTR = [
  'class',
  'id',
  'title',
  'lang',
  'dir',
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'width',
  'height',
  'loading',
  'start',
  'type',
  'value'
]

/**
 * The attributes the https-only rule applies to — and ONLY these.
 *
 * WHY THIS IS A HOOK AND NOT `ALLOWED_URI_REGEXP`. This profile used to pass
 * `ALLOWED_URI_REGEXP: /^https:\/\//i`. DOMPurify tests that regexp against EVERY
 * attribute value, not just URL-bearing ones, so any value not beginning with
 * `https://` was dropped — silently taking `lang`, `dir`, `start`, `width`,
 * `height` and `loading` out of `BLOG_ALLOWED_ATTR` despite them being listed
 * there. An ordered list with `start="3"` renumbered itself from 1.
 *
 * Scoping the scheme check to `href` / `src` mirrors the backend's
 * `allowedSchemesAppliedToAttributes: ['href', 'src']` in
 * `sanitize-rendered-html.ts`, and reusing `isHttpsUrl` — the same predicate the
 * backend and `BlogPostContentSchema` use — means the two cannot disagree about
 * what a safe URL is. It is also STRICTER than the old regexp in one place that
 * matters: DOMPurify's default config allows `data:` URIs on `img`, which the old
 * regexp did not reach and which the backend rejects.
 */
const BLOG_URI_ATTRS = new Set(['href', 'src'])

/**
 * Drop `href` / `src` values that are not absolute https URLs. `javascript:`,
 * `data:`, `http:`, protocol-relative `//host` and site-relative `/path` all fail
 * `isHttpsUrl`, so all five are removed; every other allow-listed attribute is
 * left alone.
 */
function enforceHttpsUriAttributes(
  _node: Node,
  data: { attrName: string; attrValue: string; keepAttr: boolean }
): void {
  if (!BLOG_URI_ATTRS.has(data.attrName)) return
  if (!isHttpsUrl(data.attrValue)) data.keepAttr = false
}

/**
 * Sanitize the baked HTML of a marketing blog post for render (KTD5).
 *
 * WHY THIS IS A SEPARATE PROFILE AND NOT A FIX TO `sanitizeContentHtml`.
 * `sanitizeContentHtml` passes `USE_PROFILES: { html: true }`, which in DOMPurify
 * OVERRIDES `ALLOWED_TAGS` / `ALLOWED_ATTR` — so its hand-written allow-list is
 * currently inert and it is really running DOMPurify's whole-HTML profile.
 * Removing that flag is correct but would start enforcing the real allow-list
 * across every existing CMS surface in the dashboard at once, so it is a
 * separate change. This function is ADDITIVE and blog-scoped: it names its own
 * allow-list and passes NO `USE_PROFILES`, so the list below is the one actually
 * in force here, and blog rendering keeps working whichever way that fix lands.
 *
 * Defense in depth, not the only defense: published HTML has already passed the
 * backend's publish-time sanitizer. Draft preview HTML has NOT — it is baked in
 * the admin screen and rendered straight back — so for that surface this is the
 * only sanitizer in the path.
 *
 * THE HOOK IS REGISTERED PER CALL AND REMOVED IN A `finally`. DOMPurify hooks are
 * global to the shared instance, so leaving one armed would apply the blog's
 * https rule to `sanitizeContentHtml` too. `DOMPurify.sanitize` is synchronous, so
 * nothing can interleave between the add and the remove.
 */
export function sanitizeBlogHtml(input: string | null | undefined): string {
  if (!input) return ''

  DOMPurify.addHook('uponSanitizeAttribute', enforceHttpsUriAttributes)
  try {
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: BLOG_ALLOWED_TAGS,
      ALLOWED_ATTR: BLOG_ALLOWED_ATTR,
      FORBID_TAGS,
      FORBID_ATTR: [],
      KEEP_CONTENT: true
    })
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute')
  }
}

// ============================================
// Docs profile — baked documentation page HTML (U7, R13)
// ============================================

/**
 * Tags `blocksToFullHTML` emits for the DOCS vocabulary (`DOCS_BLOCK_TYPES`).
 *
 * DERIVED FROM A REAL RENDER, not from the tag names a reader would guess. Every
 * entry below was observed in the output of rendering a document containing all
 * sixteen block types, and that render is pinned in
 * `components/docs-admin/render/__tests__/docs-rendered-markup.test.tsx` — so a
 * BlockNote upgrade that changes the markup fails a test rather than silently
 * flattening a construct on the reader's page.
 *
 * THREE ENTRIES THAT LOOK WRONG AND ARE NOT:
 *
 *   - NO `ul` / `ol` / `li`. BlockNote's full-HTML export does not use list
 *     elements at all — a `bulletListItem` is a
 *     `<div class="bn-block-content" data-content-type="bulletListItem"><p>`,
 *     and the bullet is a CSS `::before`. Allow-listing list tags here would
 *     admit markup nothing in the path produces.
 *   - `input` IS here, because a `checkListItem` renders a real checkbox. It is
 *     the one tag where allow-listing is not enough on its own — see
 *     `forceInertCheckbox` below.
 *   - `tbody` is not emitted and is here anyway: DOMPurify parses into a real
 *     DOM and the HTML parser inserts a `tbody` around bare `tr`s.
 */
export const DOCS_ALLOWED_TAGS = [
  'div',
  'p',
  'br',
  'hr',
  'span',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'del',
  'mark',
  'sub',
  'sup',
  'code',
  'pre',
  'blockquote',
  'a',
  'img',
  'video',
  'figure',
  'figcaption',
  'input',
  // U3's table block. A tag missing here does not error — it flattens the table
  // to a run of prose, silently, on the published page only.
  'table',
  'colgroup',
  'col',
  'tbody',
  'tr',
  'th',
  'td'
]

/**
 * `class` carries BlockNote's `bn-*` hooks and the container blocks' own
 * `docs-callout` / `docs-step` classes; `data-*` (allowed by DOMPurify's
 * `ALLOW_DATA_ATTR`, on by default) carries `data-content-type`,
 * `data-variant`, `data-level`, `data-checked` and `data-index`, which every
 * `.docs-page-body` rule in `globals.css` keys off. Stripping either renders the
 * page unstyled and collapses the callout box entirely.
 *
 * `style` is NOT here, matching the blog and the spine's Rule 5. BlockNote does
 * emit inline style on three elements, and `.docs-page-body` CSS stands in for
 * all three rather than the sanitizer relaxing: the file-block `figure`'s
 * `width: fit-content`, the table's `--default-cell-min-width`, and the
 * editor-only `.bn-resize-handle` bars, which without their `display: none`
 * would paint two black rectangles over every image on every published page.
 */
export const DOCS_ALLOWED_ATTR = [
  'class',
  'id',
  'title',
  'lang',
  'dir',
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'width',
  'height',
  'loading',
  'draggable',
  'controls',
  // Table cell spans (U3). A merged cell whose `colspan` is stripped does not
  // fail loudly — the row silently gains a column and the grid shears.
  'colspan',
  'rowspan',
  // The checklist checkbox, and only that — see `forceInertCheckbox`.
  'type',
  'checked',
  'disabled'
]

/**
 * `FORBID_TAGS` minus `input`, which this vocabulary legitimately emits.
 *
 * `form` stays forbidden, and that pairing is deliberate: an input that cannot
 * be inside a form has nowhere to submit to.
 */
const DOCS_FORBID_TAGS = FORBID_TAGS.filter(tag => tag !== 'input')

/**
 * THE URL POLICY IS TWO PREDICATES, and unifying them would break the docs site.
 *
 * A link `href` takes an absolute https URL OR a root-relative path
 * (`isDocsLinkHref`); a `src` must be absolute https (`isHttpsUrl`). That
 * asymmetry is Rule P1's, user-directed on 2026-08-09: documentation is mostly
 * internal cross-references — 17 of the corpus's 20 links are `/docs/...` — while
 * a media source is FETCHED and must name its origin. A sanitizer that applied
 * the https rule to `href` would strip every internal cross-reference on the
 * site, and a "fix" that applied the link rule to `src` would let a root-relative
 * media URL resolve against whatever host the page is served from.
 *
 * Both predicates are imported from shared-types — the same ones
 * `DocsContentSchema` and the backend's publish-time sanitizer use — so the
 * schema, the wire, and the render cannot disagree about what a safe URL is.
 * `javascript:`, `data:`, `http:`, and protocol-relative `//host` fail both.
 */
// A `Map`, not an object literal: the key is an attacker-supplied attribute
// name, and an object lookup resolves `constructor` / `toString` to
// `Object.prototype` members — which this would then call as if they were the
// predicate.
const DOCS_URI_ATTR_PREDICATES = new Map<string, (value: string) => boolean>([
  ['href', isDocsLinkHref],
  ['src', isHttpsUrl]
])

function enforceDocsUriAttributes(_node: Node, data: { attrName: string; attrValue: string; keepAttr: boolean }): void {
  const predicate = DOCS_URI_ATTR_PREDICATES.get(data.attrName)
  if (predicate && !predicate(data.attrValue)) data.keepAttr = false
}

/**
 * Force every surviving `input` to the inert checkbox a `checkListItem` renders.
 *
 * The attribute allowlist already omits `name`, `value` and `placeholder`, so a
 * text field could not carry data anywhere — but an unstyled text box on a
 * documentation page is a credential-harvesting prop whether or not it submits.
 * Rewriting the two attributes that decide what the element IS costs nothing (a
 * real checklist checkbox already carries both) and closes the whole shape.
 *
 * `afterSanitizeAttributes` rather than `uponSanitizeAttribute`: it runs once per
 * element, after the allowlist has been applied, which is where an attribute may
 * be ADDED.
 */
function forceInertCheckbox(node: Element): void {
  if (node.nodeName !== 'INPUT') return
  node.setAttribute('type', 'checkbox')
  node.setAttribute('disabled', '')
}

/**
 * Sanitize the baked HTML of a documentation page for render (R13, KTD2).
 *
 * WHY THIS IS A SEPARATE PROFILE AND NOT A FIX TO `sanitizeContentHtml`.
 * `sanitizeContentHtml` passes `USE_PROFILES: { html: true }`, which in DOMPurify
 * OVERRIDES `ALLOWED_TAGS` / `ALLOWED_ATTR` — so its hand-written allow-list is
 * inert and it is really running DOMPurify's whole-HTML profile. Removing that
 * flag is correct but would start enforcing the real allow-list across every
 * existing CMS surface in the dashboard at once, so it is a separate change with
 * its own blast radius. This function is ADDITIVE and docs-scoped: it names its
 * own allow-list and passes NO `USE_PROFILES`, so the list above is the one
 * actually in force here. `__tests__/html.test.ts` demonstrates the difference
 * rather than asserting it — it runs the same input through this configuration
 * with `USE_PROFILES` added back and shows the output changes.
 *
 * Defense in depth, not the only defense: published HTML has already passed the
 * backend's publish-time sanitizer (`services/docs/render/sanitize-docs-html.ts`).
 * Draft preview HTML has NOT — it is rendered in the admin screen and handed
 * straight back — so for that surface this is the only sanitizer in the path.
 *
 * THE HOOKS ARE REGISTERED PER CALL AND REMOVED IN A `finally`. DOMPurify hooks
 * are global to the shared instance, so leaving one armed would apply the docs
 * URL rule to `sanitizeContentHtml` too. `DOMPurify.sanitize` is synchronous, so
 * nothing can interleave between the add and the remove.
 */
export function sanitizeDocsHtml(input: string | null | undefined): string {
  if (!input) return ''

  DOMPurify.addHook('uponSanitizeAttribute', enforceDocsUriAttributes)
  DOMPurify.addHook('afterSanitizeAttributes', forceInertCheckbox)
  try {
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: DOCS_ALLOWED_TAGS,
      ALLOWED_ATTR: DOCS_ALLOWED_ATTR,
      FORBID_TAGS: DOCS_FORBID_TAGS,
      FORBID_ATTR: [],
      KEEP_CONTENT: true
    })
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes')
    DOMPurify.removeHook('uponSanitizeAttribute')
  }
}

/**
 * Sanitize CMS-authored rich-text HTML intended for a short preview surface.
 * Currently identical to sanitizeContentHtml; kept as a distinct symbol so we
 * can tighten the preview allow-list later (e.g. drop images) without
 * touching every call site.
 */
export function sanitizeForPreview(input: string | null | undefined): string {
  return sanitizeContentHtml(input)
}

/**
 * Return the length of the plain-text content of an HTML string after
 * sanitization. Does NOT mount the string into a live DOM, so dangerous
 * attributes never fire as a side effect of measurement.
 */
export function safeHtmlLength(input: string | null | undefined): number {
  return htmlToText(input).length
}

/**
 * Sanitize HTML and return only the text content. Safe replacement for
 * tempDiv.innerHTML / tempDiv.textContent patterns used purely for measuring
 * or truncating untrusted markup.
 */
export function htmlToText(input: string | null | undefined): string {
  if (!input) return ''
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true
  }).trim()
}

/**
 * Truncate sanitized HTML to a maximum number of TEXT characters. Operates on
 * the sanitized plain-text form, so the result is always a safe plain string
 * (no half-cut tags, no orphaned attribute fragments). The result is the
 * truncated text + an ellipsis, NOT HTML — render as plain text, not via
 * HTML injection.
 */
export function truncateHtmlToText(input: string | null | undefined, maxLength: number): string {
  const text = htmlToText(input)
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

// ============================================
// Icon profile — school-uploaded library SVG
// ============================================

/**
 * Sanitize a school icon-library entry's markup for render in the dashboard.
 *
 * The backend already sanitizes on WRITE (the icon-library pipeline: DOMPurify
 * over jsdom, structural rejection of gradients/patterns/masks/filters/raster,
 * style flattening, then recolor). This is the second half of a
 * defence-in-depth pair, not a duplicate: a school admin is only semi-trusted
 * and is the sole actor who can put bytes into that pipeline, and platform
 * privilege is a global flag — so markup that survived a future bypass on the
 * write path would otherwise execute with whatever privilege the person
 * viewing that school's dashboard holds. Re-sanitizing at the render boundary
 * makes stored payloads inert without a data migration, exactly as the
 * module docblock describes for CMS HTML.
 *
 * Deliberately NOT one of the HTML profiles: those allow-lists name no svg
 * element, so routing icon markup through them strips the icon to nothing.
 * This is the svg profile plus explicit removal of the escapes that profile
 * leaves open — foreign objects (an HTML re-entry point), and animation
 * elements, whose `values`/`from`/`to` can drive an attribute after render.
 * `use` is dropped because a same-document reference is meaningless once the
 * markup is inlined, and a cross-document one is a fetch.
 */
export function sanitizeIconSvg(input: string | null | undefined): string {
  if (!input) return ''
  return DOMPurify.sanitize(input, {
    USE_PROFILES: { svg: true, svgFilters: false },
    FORBID_TAGS: ['foreignObject', 'script', 'style', 'use', 'animate', 'animateTransform', 'animateMotion', 'set'],
    FORBID_ATTR: ['href', 'xlink:href', 'filter', 'style'],
    ALLOW_DATA_ATTR: false
  })
}
